import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Text,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import moment from 'moment'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { BsFillXDiamondFill as BsFillXDiamondFillIcon } from 'react-icons/bs'
import { colors } from '../../../../ui-kit'
import SearchBar from '../../SearchBar'
import type { EmbeddedMetadataValues } from '../../../interfaces/FileDetailsResponse'

const INITIAL_ITEMS_LENGTH = 6

const EmbeddedMetadata = ({
  data,
}: {
  data: EmbeddedMetadataValues | null
}): ReactElement => {
  const { t } = useTranslation()
  const [moreItemsVisible, toggleMoreItems] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const formatString = (key: string, val): string => {
    switch (true) {
      case [
        'SubSecDateTimeOriginal',
        'DateTimeCreated',
        'DateTimeOriginal',
      ].includes(key): {
        return moment(val).format('ddd, MMMM Do YYYY, HH:mm:ss')
      }
      case key === 'TimeCreated': {
        return moment(val, 'HH:mm:ssZ').format('HH:mm:ss')
      }
      case ['ModifyDate', 'ArtworkDateCreated', 'DateCreated'].includes(key): {
        return moment(val).format('MMMM Do YYYY')
      }
      default:
        return String(val)
    }
  }

  const metadataMapper = (entry): ReactElement => {
    const [key, val] = entry as [string, unknown]
    return (
      <Flex marginBottom="10px" flexWrap="wrap" key={key}>
        <Text marginRight="5px">{key} :</Text>
        <Text as="b">{formatString(key, val)}</Text>
      </Flex>
    )
  }

  const itemsData = data ? Object.entries(data) : []
  const filteredItemsData = itemsData.filter(([key, val]) =>
    searchQuery
      ? key.toUpperCase().includes(searchQuery.toUpperCase()) ||
        JSON.stringify(val).toUpperCase().includes(searchQuery.toUpperCase())
      : true,
  )

  const initialItems: ReactElement[] = filteredItemsData
    .slice(0, INITIAL_ITEMS_LENGTH)
    .map(metadataMapper)
  const moreItems: ReactElement[] = filteredItemsData
    .slice(INITIAL_ITEMS_LENGTH)
    .map(metadataMapper)
  const totalItems = filteredItemsData.length

  return (
    <AccordionItem borderTopWidth={0} borderBottomWidth={1}>
      <AccordionButton py={6}>
        <Flex
          flex="1"
          textAlign="left"
          lineHeight="1.5em"
          fontWeight="semibold"
          alignItems="center"
          style={{ gap: '1em' }}
        >
          <BsFillXDiamondFillIcon fontSize="1.5em" />
          {t('embedded_metadata', 'Embedded Metadata')}
        </Flex>
        <AccordionIcon color={colors.appSlate} display="block" />
      </AccordionButton>

      <AccordionPanel>
        <Flex direction="column" maxHeight="300px" overflow="auto">
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            padding="1.5em 0em"
          />
          {initialItems.length ? initialItems : <Text>No data available</Text>}
          {moreItemsVisible && moreItems.length ? (
            <React.Fragment>
              {moreItems}
              <Text
                textAlign="right"
                cursor="pointer"
                color={colors.appBlue}
                onClick={() => void toggleMoreItems(false)}
              >
                - See less
              </Text>
            </React.Fragment>
          ) : null}
          {!moreItemsVisible && moreItems.length ? (
            <Text
              textAlign="right"
              cursor="pointer"
              color={colors.appBlue}
              onClick={() => void toggleMoreItems(true)}
            >
              - See all {totalItems} items
            </Text>
          ) : null}
        </Flex>
      </AccordionPanel>
    </AccordionItem>
  )
}

export default EmbeddedMetadata
